import { Search, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

import { Input } from './input';

export const SearchField = React.forwardRef<
    HTMLInputElement,
    Omit<React.ComponentProps<typeof Input>, 'type' | 'value' | 'onChange'> & {
        value: string;
        onValueChange: (value: string) => void;
        onClear?: () => void;
        inputClassName?: string;
    }
>(
    (
        { value, onValueChange, onClear, inputClassName, className, onKeyDown, disabled, ...rest },
        forwardedRef,
    ) => {
        const inputRef = React.useRef<HTMLInputElement>(null);
        React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

        const clear = () => {
            onValueChange('');
            onClear?.();
            inputRef.current?.focus();
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(event);
            if (event.key === 'Escape' && value !== '') {
                event.preventDefault();
                event.stopPropagation();
                clear();
            }
        };

        return (
            <div className={cn('relative', className)}>
                <Search
                    aria-hidden="true"
                    className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-neutral-500"
                />
                <Input
                    ref={inputRef}
                    type="search"
                    autoComplete="off"
                    spellCheck={false}
                    enterKeyHint="search"
                    {...rest}
                    disabled={disabled}
                    value={value}
                    onChange={(event) => onValueChange(event.target.value)}
                    className={cn(
                        'text-ellipsis pl-9 [&::-webkit-search-cancel-button]:appearance-none',
                        value !== '' && 'pr-10',
                        inputClassName,
                    )}
                    onKeyDown={handleKeyDown}
                />
                {value !== '' && !disabled ? (
                    <button
                        type="button"
                        aria-label="Clear search"
                        className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={clear}
                    >
                        <X className="size-4" aria-hidden="true" />
                    </button>
                ) : null}
            </div>
        );
    },
);
SearchField.displayName = 'SearchField';
